import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, Crosshair } from "lucide-react";

// Load only the libraries we actually use; single loader across every Google-
// Maps-powered component in the app so we don't blow the free-tier quota with
// duplicate script tags.
const GOOGLE_LIBRARIES = ["places"];

const CONTAINER_STYLE = { width: "100%", height: "420px", borderRadius: "12px" };
// Jaipur as India-first default; picker recentres to `hostel.map_lat/map_lng`
// as soon as it opens if they exist.
const DEFAULT_CENTER = { lat: 26.9124, lng: 75.7873 };

/**
 * Modal that lets an owner set an exact map pin (lat/lng + human-readable
 * address) for a given hostel using Google Maps + Places Autocomplete.
 *
 * Props preserved from the previous Leaflet version so callers (OwnerDashboard)
 * keep working without changes:
 *   { hostel, open, onClose, onSaved }
 */
export default function HostelLocationEditor({ hostel, open, onClose, onSaved }) {
  const initialLat = Number(hostel?.map_lat ?? hostel?.lat ?? DEFAULT_CENTER.lat);
  const initialLng = Number(hostel?.map_lng ?? hostel?.lng ?? DEFAULT_CENTER.lng);
  const initialAddr = hostel?.map_address || hostel?.address || "";

  const [pin, setPin] = useState({ lat: initialLat, lng: initialLng });
  const [address, setAddress] = useState(initialAddr);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-maps-loader",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_LIBRARIES,
  });

  const initialCenter = useMemo(() => ({ lat: initialLat, lng: initialLng }), [initialLat, initialLng]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    // Cache one Geocoder instance for click-to-reverse-geocode.
    geocoderRef.current = new window.google.maps.Geocoder();
  }, []);

  // Debounced Places (New API) predictions
  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places?.AutocompleteSuggestion) return;
    if (!query || query.trim().length < 2) { setPredictions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { suggestions } = await window.google.maps.places
          .AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: query,
            includedRegionCodes: ["in"],
          });
        const norm = (suggestions || []).slice(0, 6).map((s) => {
          const p = s.placePrediction;
          return {
            id: p?.placeId,
            place: p,
            main: p?.mainText?.text || p?.text?.text || "",
            sub: p?.secondaryText?.text || "",
          };
        });
        setPredictions(norm);
      } catch {
        setPredictions([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [query, isLoaded]);

  const choosePrediction = async (item) => {
    setDropdownOpen(false);
    setQuery(`${item.main}${item.sub ? ", " + item.sub : ""}`);
    try {
      // Fetch the geometry for the chosen place
      const place = item.place.toPlace();
      await place.fetchFields({ fields: ["location", "displayName", "formattedAddress"] });
      const loc = place.location;
      const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
      setPin({ lat, lng });
      setAddress(place.formattedAddress || `${item.main}, ${item.sub}`.trim());
      if (mapRef.current) { mapRef.current.panTo({ lat, lng }); mapRef.current.setZoom(17); }
    } catch (e) {
      toast.error("Could not resolve that place. Try clicking on the map instead.");
    }
  };

  const reverseGeocode = (lat, lng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results?.[0]) setAddress(results[0].formatted_address);
    });
  };

  const onMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setPin({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) return toast.error("Geolocation is not available in this browser.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPin({ lat, lng });
        if (mapRef.current) { mapRef.current.panTo({ lat, lng }); mapRef.current.setZoom(17); }
        reverseGeocode(lat, lng);
        setLocating(false);
      },
      (err) => { setLocating(false); toast.error(`Location denied: ${err.message}`); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const save = async () => {
    if (!address || address.trim().length < 5) return toast.error("Address is too short. Search or click on the map to drop a pin.");
    setSaving(true);
    try {
      const { data } = await api.post(`/owner/hostels/${hostel.id}/location`, {
        lat: Number(pin.lat.toFixed(6)),
        lng: Number(pin.lng.toFixed(6)),
        address: address.trim(),
      });
      toast.success("Location saved. Students will see this exact spot in their booking confirmation.");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="hostel-location-editor"
        className="max-w-4xl w-[95vw] p-0 overflow-hidden"
      >
        <DialogHeader className="px-5 pt-5 pb-3 flex flex-row items-center justify-between gap-2 space-y-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MapPin size={18} className="text-vermilion-600" />
            Set exact location — {hostel?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-3 grid gap-3">
          {loadError && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3">
              Failed to load Google Maps. Please refresh and try again.
            </div>
          )}

          {!isLoaded ? (
            <div className="h-[420px] flex items-center justify-center bg-slate-50 rounded-xl border border-slate-200 text-slate-500">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading map…
            </div>
          ) : (
            <>
              <div className="flex gap-2 flex-wrap relative">
                <div className="relative flex-1 min-w-[220px]">
                  <Input
                    data-testid="location-search-input"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setDropdownOpen(true); }}
                    onFocus={() => setDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                    placeholder="Search address, coaching centre, or landmark…"
                    className="w-full"
                  />
                  {dropdownOpen && predictions.length > 0 && (
                    <div
                      data-testid="location-predictions"
                      className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-40 max-h-64 overflow-y-auto"
                    >
                      {predictions.map((p, i) => (
                        <button
                          key={p.id || i}
                          type="button"
                          data-testid={`location-prediction-${i}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => choosePrediction(p)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2"
                        >
                          <MapPin size={14} className="mt-1 text-sky-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{p.main}</div>
                            {p.sub && <div className="text-xs text-slate-500 truncate">{p.sub}</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="location-use-my-location"
                  onClick={useMyLocation}
                  disabled={locating}
                  className="rounded-full gap-1"
                >
                  <Crosshair size={14} /> {locating ? "Locating…" : "Use my location"}
                </Button>
              </div>

              <GoogleMap
                mapContainerStyle={CONTAINER_STYLE}
                center={initialCenter}
                zoom={pin.lat === DEFAULT_CENTER.lat ? 11 : 16}
                onLoad={onMapLoad}
                onClick={onMapClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker
                  position={pin}
                  draggable
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat();
                    const lng = e.latLng.lng();
                    setPin({ lat, lng });
                    reverseGeocode(lat, lng);
                  }}
                />
              </GoogleMap>

              <div className="grid gap-1">
                <label className="text-xs tracking-widest uppercase font-bold text-slate-600">Address on your listing</label>
                <textarea
                  data-testid="location-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={2}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Full street address — students see this on their booking confirmation."
                />
                <div className="text-[11px] text-slate-500 font-mono">
                  pin: {pin.lat.toFixed(6)}, {pin.lng.toFixed(6)}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="px-5 pb-5 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            data-testid="location-save-btn"
            onClick={save}
            disabled={saving || !isLoaded}
            className="rounded-full bg-orange-800 hover:bg-orange-900 text-white gap-1"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save location"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
